
import { FormSectionHandler } from '../entity.handler';
import { DeviceTypeResponse, IoTypeListItem } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, ComboField, SectionField, NumberField, CheckBoxField, TextField } from '../utils';
import { DeviceParametersResponse, DeviceParametersUpdateRequest, getDeviceTypeIdFromBulk } from './parameters.section';
import { KeyFormBuilderComboFieldComponent } from 'app/shared/components/form-builder/combo/combo.component';
import { getDeviceIOWhitelist } from './io-whitelist.section';


export type InputType = 'analogs' | 'cameras' | 'cans' | 'counters' | 'digitals' | 'outputs' | 'temperatures' | 'values';
export const InputTypeValues: InputType[] = ['analogs', 'cameras', 'cans', 'counters', 'digitals', 'outputs', 'temperatures', 'values'];

const InputTypeIcons = {
    analogs: 'tachometer',
    cameras: 'camera-alt',
    cans: 'oil-can',
    counters: 'abacus',
    digitals: 'arrow-to-left',
    outputs: 'arrow-from-left',
    temperatures: 'thermometer-half',
    values: 'list-alt',
};

const InputTypeIoType = {
    analogs: 'analog_input',
    cameras: 'camera_input',
    cans: 'can_input',
    counters: 'counter_input',
    digitals: 'digital_input',
    outputs: 'digital_output',
    temperatures: 'temperature_input',
    values: 'value_input',
};



interface EditorValues {
    [inputTypeId: string]: {
        input: string,
        [key: string]: any,
    };
}

export class DeviceIoSectionHandler implements FormSectionHandler<DeviceParametersResponse, EditorValues> {

    type = 'form';
    icon = InputTypeIcons[this.inputType];
    bulk = true;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    ioKey = InputTypeIoType[this.inputType];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public inputType: InputType,
        private applyUpdateCallback: (entity: any, payload: any) => Promise<DeviceParametersResponse>, 
    ) {

    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.IO_${this.inputType.toUpperCase()}.${key}`, values);
    }

    applyUpdate(entity: DeviceParametersResponse, payload: DeviceParametersUpdateRequest): Promise<DeviceParametersResponse> {
        return this.applyUpdateCallback(entity, payload);
    }

    mapEntityToEditor(entity: DeviceParametersResponse): Promise<EditorValues> {
        if (entity && entity.parameters && entity.parameters.io) {
            return Promise.resolve({ ...entity.parameters.io[this.ioKey] });
        } else {
            return Promise.resolve({});
        }
    }

    mapEditorToEntity(entity: DeviceParametersUpdateRequest, values: EditorValues): Promise<DeviceParametersUpdateRequest> {
        entity.parameters = entity.parameters || { io: {} };
        entity.parameters.io = entity.parameters.io || {};
        entity.parameters.io[this.ioKey] = values;
        return Promise.resolve(entity);
    }

    getFields(entity: DeviceParametersResponse): Promise<SectionField[]> {

        const deviceTypeId = getDeviceTypeIdFromBulk(entity ? [entity] : this.admin.selectedItems);
        if (deviceTypeId) {
            this.hidden = false;

            const ioKey = InputTypeIoType[this.inputType];
            return Promise.all([
                this.admin.getCached(`getDeviceType/${deviceTypeId}/${this.ownerId}`, api => api.entities.getDeviceType(deviceTypeId, this.ownerId)),
                this.getIoTypes(ioKey),
            ]).then(([deviceType, ioTypes]) => {

                let inputs = getDeviceIOWhitelist(deviceType, entity, ioKey); 

                const getLookupValues = (custom: string[]) => {
                    const lookupValues = [
                        { key: 'none', value: this.admin.i18n.instant('SHARED.NONE') },
                        ...inputs.map(key => ({ key: key, value: key })).sort((a, b) => a.value.localeCompare(b.value)),
                        ...custom.filter(x => !inputs.includes(x)).map(key => ({ key: key, value: key })),
                        { key: '_custom_', value: `${this.admin.translate('EDITORS.DEVICE.IO.CUSTOM')}...` },
                    ];
                    if (lookupValues.length === 1 || ioTypes.length === 0) {
                        this.hidden = true;
                    }
                    return lookupValues;
                };

                ioTypes.sort((a, b) => a.name.localeCompare(b.name));

                // builds fields dynamically from the input types that have been configured for this client
                return ioTypes.map(ioType => ({
                    ...ComboField(ioType.id, ioType.name, true, getLookupValues([])),
                    getValue: (field: FormBuilderField, values: any) => (values[field.id] && values[field.id].input) || 'none',
                    getText: (field: FormBuilderField, values: any) => {

                        const value = values[field.id] && values[field.id].input;
                        if (value) {
                            let warning = '';
                            // if we have a whitelist defined, check whether this input is actually in said whitelist, or show a warning
                            if (entity && !inputs.includes(value)) {
                                warning = `<i class="icon icon-triangle-exclamation text-warning" title="${this.admin.translate('EDITORS.DEVICE.IO.WHITELIST_WARNING')}" style="font-size: 1.25rem;"></i>&nbsp;`;
                            }
                            return `${warning}${value}`;
                        } 
                        return value;
                    },
                    setValue: (field: FormBuilderField, values: any, value: any) => {

                        if (value === 'none') {
                            values[field.id] = null;
                            return Promise.resolve();
                        } else {
                            if (value && value !== (values[field.id] && values[field.id].input)) {
                                const def = this.getDefaultCalibration();
                                if (def) {
                                    return this.showCalibrationModal(deviceType, ioType, { input: value === '_custom_' ? '' : value, ...def }).then(result => {
                                        values[field.id] = result;
                                        if (result && field.component instanceof KeyFormBuilderComboFieldComponent) {
                                            field.component.comboValues = getLookupValues([result.input]);
                                        }
                                    });
                                } else {
                                    values[field.id] = { input: value };
                                    return Promise.resolve();
                                }
                            }
                        }
                    },
                    actions: [
                        {
                            icon: 'cog', name: this.admin.i18n.instant('ADMIN.EDITOR.SETTINGS'),
                            click: (_action: string, field: FormBuilderField, values: any) => {
                                const value = values[field.id];
                                if (value) {
                                    return this.showCalibrationModal(deviceType, ioType, values[field.id]).then(result => {
                                        if (result) {
                                            values[field.id] = result;
                                            if (field.component instanceof KeyFormBuilderComboFieldComponent) {
                                                field.component.comboValues = getLookupValues([result.input]);
                                            }
                                        }
                                    });
                                }
                            },
                        },
                    ],

                }));

            }).then(fields => EntityUtils.prepareBulk(!entity, fields.map(x => x.id), fields));

        } else {
            this.hidden = true;
            return Promise.resolve([]);
        }

    }

    getIoTypes(type: string): Promise<IoTypeListItem[]> {
        return this.admin.getCached(`ioTypes.${this.ownerId}.true`, (api) => api.entities.listIoTypes(this.ownerId, true, 0, 300)).then(ioTypes => {
            return ioTypes.items.filter(x => x.state === 'active' && (!type || x.type === type)).sort((a, b) => a.name.localeCompare(b.name));
        });
    }

    getDefaultCalibration() {
        switch (this.inputType) {
            case 'analogs': return { minVolts: 0, maxVolts: 100, minValue: 0, maxValue: 100 };
            case 'cans': return { minValue: 0, maxValue: 100, multiplier: 1 };
            case 'counters': return { multiplier: 1 };
            case 'digitals': return { invert: false };
            case 'outputs': return { invert: false, pulsed: false };
            case 'cameras': return {};
            case 'temperatures': return {};
            case 'values': return {};
            default:
                return null;
        }
    }

    showCalibrationModal(deviceType: DeviceTypeResponse, ioType: IoTypeListItem, input: any): Promise<any> {


        const translate = (key: string, values?: Object): string => {
            return this.admin.translate(`EDITORS.DEVICE.IO.CALIBRATION.${key}`, values);
        };

        const fd = {
            input: TextField('input', translate('FIELDS.INPUT'), true, 1, 255),
            minVolts: NumberField('minVolts', translate('FIELDS.MIN_VOLTS'), true, -2000000, 2000000, 'volts'),
            maxVolts: NumberField('maxVolts', translate('FIELDS.MAX_VOLTS'), true, -2000000, 2000000, 'volts'),
            minValue: NumberField('minValue', translate('FIELDS.MIN_VALUE'), true, -2000000, 2000000, ioType.unit),
            maxValue: NumberField('maxValue', translate('FIELDS.MAX_VALUE'), true, -2000000, 2000000, ioType.unit),
            multiplier: NumberField('multiplier', translate('FIELDS.MULTIPLIER'), true, -2000000, 2000000, undefined),
            invert: CheckBoxField('invert', translate('FIELDS.INVERT'), true),
            pulsed: CheckBoxField('pulsed', translate('FIELDS.PULSED'), true),
            enableLiveStream: CheckBoxField('enableLiveStream', translate('FIELDS.ENABLE_LIVE_STREAM'), true),
        };

        const getFields = (): FormBuilderField[] => {
            switch (this.inputType) {
                case 'analogs': return [fd.input, fd.minVolts, fd.maxVolts, fd.minValue, fd.maxValue];
                case 'cans': return [fd.input, fd.minValue, fd.maxValue, fd.multiplier];
                case 'counters': return [fd.input, fd.multiplier];
                case 'digitals': return [fd.input, fd.invert];
                case 'outputs': return [fd.input, fd.invert, fd.pulsed];
                case 'cameras':
                    if (this.admin.user.owner.type === 'system') { // only allow system users this option for now
                        // determine if we need settings for live streaming by inspecting the accessories on the deviceType
                        for (const accessoryId of Object.keys(deviceType?.accessories || {})) {
                            const accessory = deviceType.accessories[accessoryId];
                            if (accessory?.ioCapabilities?.camera_input[input?.input || '']) {
                                // this input is part of this accessory
                                if (accessory.features?.video_live_stream) {
                                    // this input is on an accessory that supports live streaming
                                    return [fd.input, fd.enableLiveStream];
                                }
                            }
                        }
                    }
                    return [fd.input]; 
                case 'temperatures': return [fd.input];
                case 'values': return [fd.input];
                default:
                    return null;
            }
        };

        return this.admin.showFormModal<any>({
            groups: [{
                name: translate('TITLE', { type: ioType.name }),
                description: translate('DESC'),
                fields: getFields(),
            }],
        }, input);

    }

}
